import React, { lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Info, Send, X } from 'lucide-react';
import toast from 'react-hot-toast';
import PageHeader from '../components/Common/PageHeader';
import AssistantReply from '../components/assistant/AssistantReply';
import { useTranslation } from 'react-i18next';
import { assistantService } from '../services/api';
import { userFacingError } from '../i18n/userFacingError';
import { choicesForMessage, startsNewFlow } from '../utils/assistantFlow';

const LinaAvatar = lazy(() => import('../components/lina/LinaAvatar'));

/**
 * Traduz o rótulo do atalho, mas nunca o texto enviado: o roteador do gateway
 * (AMBIENTAL_TOOL_PATTERNS) casa por expressões em português.
 */
const CHOICE_LABEL_KEY = {
  expiring: 'choiceExpiring',
  licenses: 'choiceLicenses',
  condicionantes: 'choiceCondicionantes',
  legislation: 'choiceLegislation',
  processes: 'choiceProcesses',
  complaints: 'choiceComplaints',
  enterprise: 'choiceEnterprise',
  draft_license: 'choiceDraftLicense',
  draft_complaint: 'choiceDraftComplaint',
  whats_next: 'choiceWhatsNext',
  permissions: 'choicePermissions',
};

/**
 * Só entra em cena se /assistant/config não responder. Fica no subconjunto de
 * leitura, liberado a qualquer papel — atalhos de escrita dependem do filtro por
 * papel que o backend aplica.
 */
const FALLBACK_CHOICES = [
  { id: 'expiring', label: 'Licenças a vencer', send: 'Quais licenças vencem' },
  { id: 'licenses', label: 'Consultar licenças', send: 'Quero ver as licenças' },
  { id: 'condicionantes', label: 'Ver condicionantes', send: 'Quero ver as condicionantes' },
  { id: 'legislation', label: 'Consultar legislação', send: 'Quero consultar a legislação' },
  { id: 'processes', label: 'Ver processos', send: 'Quero ver os processos' },
  { id: 'complaints', label: 'Ver denúncias', send: 'Quero ver as denúncias' },
  { id: 'enterprise', label: 'Ver empreendimento', send: 'Quero consultar um empreendimento' },
  { id: 'whats_next', label: 'O que tenho pendente', send: 'O que tenho pendente hoje' },
  { id: 'permissions', label: 'O que posso fazer', send: 'O que eu posso fazer' },
];

const Assistant = () => {
  const { t } = useTranslation('assistant');
  const [remoteChoices, setRemoteChoices] = useState(null);
  const [llmReady, setLlmReady] = useState(null);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId] = useState(() => `web-${Date.now()}`);
  const [flow, setFlow] = useState(null);
  const [slotValue, setSlotValue] = useState('');
  const [insight, setInsight] = useState(null);
  const [messages, setMessages] = useState(() => [{ role: 'assistant', text: t('welcome'), menu: true }]);
  const bottomRef = useRef(null);

  const localizeChoices = useCallback(
    (list) =>
      (list || []).map((choice) => {
        const key = CHOICE_LABEL_KEY[choice.id];
        return {
          id: choice.id || choice.label,
          label: key ? t(key, { defaultValue: choice.label }) : choice.label,
          send: choice.send,
        };
      }),
    [t],
  );

  const menu = useMemo(
    () => localizeChoices(remoteChoices?.length ? remoteChoices : FALLBACK_CHOICES),
    [localizeChoices, remoteChoices],
  );

  useEffect(() => {
    let active = true;
    assistantService
      .config()
      .then(({ data }) => {
        if (!active) return;
        setLlmReady(data?.ready === true);
        const remote = (data?.choices || []).filter((choice) => choice?.label && choice?.send);
        if (remote.length) setRemoteChoices(remote);
      })
      .catch(() => {
        if (active) setLlmReady(null);
      });
    assistantService
      .insight()
      .then(({ data }) => {
        if (active) setInsight(data || null);
      })
      .catch(() => {
        if (active) setInsight(null);
      });
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    const welcome = insight?.text ? `${insight.text}\n\n${t('welcome')}` : t('welcome');
    setMessages((current) =>
      current.length === 1 && current[0]?.menu
        ? [{ role: 'assistant', text: welcome, menu: true, href: insight?.href }]
        : current,
    );
  }, [t, insight]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, loading]);

  const choicesFor = (msg) => localizeChoices(choicesForMessage(msg, menu));

  const send = async (message, confirmToken) => {
    if (loading) return;
    const trimmed = (message || '').trim();
    if (!trimmed && !confirmToken) {
      toast.error(flow?.slot?.label ? t('informSlot', { label: flow.slot.label }) : t('emptyInput'));
      return;
    }
    const nextFlow = startsNewFlow(trimmed) ? null : flow;
    if (confirmToken) {
      // O token vale uma vez só; deixar o botão na tela convida a um clique morto.
      setMessages((current) =>
        current.map((item) => (item.confirmToken === confirmToken ? { ...item, confirmToken: undefined } : item)),
      );
    } else {
      setInput('');
      setSlotValue('');
      setMessages((current) => [
        ...current,
        { role: 'user', text: trimmed },
        { role: 'assistant', text: '', streaming: true },
      ]);
    }
    setLoading(true);
    try {
      const data = await assistantService.stream(trimmed || 'confirmar', {
        confirmToken,
        sessionId,
        guidedTool: nextFlow?.tool,
        draftArgs: nextFlow?.args,
        onStatus: (text) => {
          setMessages((current) => {
            const next = [...current];
            const last = next[next.length - 1];
            if (last?.role === 'assistant' && last.streaming && !last.text) {
              next[next.length - 1] = { ...last, status: text };
            }
            return next;
          });
        },
        onToken: (token) => {
          setMessages((current) => {
            const next = [...current];
            const last = next[next.length - 1];
            if (last?.role === 'assistant' && last.streaming) {
              next[next.length - 1] = { ...last, text: `${last.text || ''}${token}` };
            }
            return next;
          });
        },
      });
      const reply = {
        role: 'assistant',
        actions: data.suggested_actions || [],
        entities: data.entities || [],
        confirmToken: data.confirm_token,
        missingFields: data.missing_fields,
        choices: data.choices,
        slot: data.slot,
        showMenu: data.show_menu === true,
      };
      if (confirmToken) {
        setMessages((current) => [...current, { ...reply, text: data.reply }]);
      } else {
        setMessages((current) => {
          const next = [...current];
          const last = next[next.length - 1];
          if (last?.role === 'assistant' && last.streaming) {
            next[next.length - 1] = { ...reply, text: data.reply || last.text };
          }
          return next;
        });
      }
      if (data.missing_fields?.length) {
        setFlow({
          tool: data.guided_tool,
          args: data.draft_args || {},
          slot: data.slot || { field: data.missing_fields[0], kind: 'text', label: data.missing_fields[0] },
        });
      } else {
        setFlow(null);
      }
    } catch (error) {
      toast.error(userFacingError(error, 'assistant:error'));
      setMessages((current) => [
        ...current.filter((item) => !item.streaming),
        { role: 'assistant', text: t('errorRetry'), menu: true },
      ]);
      setFlow(null);
    } finally {
      setLoading(false);
    }
  };

  const cancelFlow = () => {
    setSlotValue('');
    setFlow(null);
    send('cancelar');
  };

  const streaming = loading || messages.some((item) => item.streaming);
  const userTurns = messages.filter((item) => item.role === 'user').length;
  const linaMood = streaming ? 'talk' : userTurns === 0 ? 'greet' : 'idle';

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
      <div className="shrink-0">
        <PageHeader title={t('title')} description={t('description')} />
      </div>
      <div className="mt-6 flex min-h-0 flex-1 flex-col overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
        <div className="flex items-center gap-3 border-b border-gray-100 bg-gradient-to-r from-primary-50/80 to-white px-4 py-3 sm:px-6">
          <Suspense fallback={<div className="lina-stage lina-stage-chat" />}>
            <LinaAvatar mood={linaMood} variant="chat" label={t('linaName')} />
          </Suspense>
          <div>
            <p className="text-sm font-semibold text-gray-900">{t('linaName')}</p>
            <p className="mt-0.5 text-xs text-gray-600">{t('linaGreeting')}</p>
          </div>
        </div>

        {llmReady === false && (
          <div className="flex items-start gap-2 border-b border-gray-100 bg-gray-50 px-4 py-3 text-xs text-gray-600 sm:px-6">
            <Info className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
            <p>{t('basicMode')}</p>
          </div>
        )}

        <div className="min-h-0 flex-1 space-y-4 overflow-y-auto bg-slate-50/70 p-4 sm:p-6">
          {messages.map((msg, index) => {
            // Atalhos de fluxo só na última resposta: repetidos a cada mensagem
            // eles viram ruído e ainda reenviam um fluxo que já andou.
            const guided = msg.role === 'assistant' && index === messages.length - 1 && !msg.streaming;
            const choices = guided ? choicesFor(msg) : [];
            return (
              <div key={index} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div
                  className={`max-w-[min(40rem,90%)] px-4 py-3.5 text-sm ${
                    msg.role === 'user'
                      ? 'rounded-2xl rounded-br-md bg-primary-700 text-white shadow-sm'
                      : 'rounded-2xl rounded-bl-md border border-slate-200 bg-white text-slate-900 shadow-soft'
                  }`}
                >
                  {msg.role === 'user' || msg.streaming ? (
                    <p className="whitespace-pre-wrap leading-relaxed">
                      {msg.streaming && !msg.text ? msg.status || t('analyzing') : msg.text}
                    </p>
                  ) : (
                    <AssistantReply
                      text={msg.text}
                      entities={msg.entities}
                      actions={msg.actions}
                      href={msg.href}
                      hrefLabel={t('insightOpenTasks')}
                    />
                  )}
                  {msg.confirmToken && (
                    <button
                      type="button"
                      className="btn btn-primary btn-sm mt-3 bg-amber-600 hover:bg-amber-700"
                      disabled={loading}
                      onClick={() => send('confirmar', msg.confirmToken)}
                    >
                      {t('confirm')}
                    </button>
                  )}
                  {guided && msg.slot && (
                    <form
                      className="mt-3 flex gap-2"
                      onSubmit={(event) => {
                        event.preventDefault();
                        send(slotValue);
                      }}
                    >
                      <input
                        type={msg.slot.kind === 'date' ? 'date' : msg.slot.kind === 'datetime' ? 'datetime-local' : 'text'}
                        value={slotValue}
                        onChange={(event) => setSlotValue(event.target.value)}
                        placeholder={t('informSlotPlaceholder', { label: msg.slot.label })}
                        className="min-h-[2.25rem] flex-1 rounded-md border border-gray-300 px-3 py-1.5 text-sm"
                        required
                        disabled={loading}
                      />
                      <button
                        type="submit"
                        className="btn btn-primary btn-sm"
                        disabled={loading || !slotValue.trim()}
                      >
                        {t('continueAction')}
                      </button>
                    </form>
                  )}
                  {choices.length > 0 && (
                    <div className="mt-3 flex flex-wrap gap-2">
                      {choices.map((choice) => (
                        <button
                          key={choice.id}
                          type="button"
                          disabled={loading}
                          onClick={() => send(choice.send)}
                          className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1.5 text-xs font-medium text-primary-800 hover:border-primary-400 hover:bg-white disabled:opacity-50"
                        >
                          {choice.label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
          {loading && !messages[messages.length - 1]?.streaming && (
            <p className="text-sm text-gray-500">{t('analyzing')}</p>
          )}
          <div ref={bottomRef} />
        </div>

        {flow?.slot && (
          <div className="flex items-center justify-between gap-3 border-t border-amber-100 bg-amber-50/70 px-4 py-2 text-xs text-amber-900">
            <span>{t('flowActive', { label: flow.slot.label })}</span>
            <button
              type="button"
              onClick={cancelFlow}
              disabled={loading}
              className="inline-flex items-center gap-1 font-medium underline disabled:opacity-50"
            >
              <X className="h-3.5 w-3.5" aria-hidden />
              {t('cancelFlow')}
            </button>
          </div>
        )}

        {userTurns > 0 && menu.length > 0 && (
          <div className="flex flex-wrap gap-2 border-t border-gray-100 bg-gray-50/80 px-4 py-3">
            {menu.map((choice) => (
              <button
                key={choice.id}
                type="button"
                disabled={loading}
                onClick={() => send(choice.send)}
                className="rounded-full border border-gray-200 bg-white px-3 py-1.5 text-xs font-medium text-primary-800 hover:border-primary-400 disabled:opacity-50"
              >
                {choice.label}
              </button>
            ))}
          </div>
        )}

        <div className="flex gap-2 border-t border-gray-100 p-4">
          <textarea
            value={input}
            onChange={(event) => setInput(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter' && !event.shiftKey) {
                event.preventDefault();
                send(input);
              }
            }}
            rows={2}
            placeholder={flow?.slot?.label ? t('informSlotPlaceholder', { label: flow.slot.label }) : t('placeholder')}
            className="min-h-[3rem] flex-1 resize-y rounded-md border border-gray-300 px-3 py-2 text-sm"
            disabled={loading}
          />
          <button
            type="button"
            onClick={() => send(input)}
            disabled={loading || !input.trim()}
            className="inline-flex items-center gap-2 self-end rounded-md bg-primary-700 px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
          >
            <Send className="h-4 w-4" />
            {t('send')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default Assistant;
